<template>
  <div class="advertisers-page">
    <b-card no-body class="p-1">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-1 top-toolbar">
        <div class="d-flex align-items-center flex-wrap left-controls">
          <v-select
            :clearable="false"
            :searchable="false"
            class="entries-select mr-1"
            :options="['08', '10', '20', '50']"
            v-model="perPage"
            @input="reloadPageOne"
          />
          <b-button variant="primary" class="add-btn" @click="openAddCompanyModal">
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            Add New Advertisers(Company)
          </b-button>
        </div>

        <div class="d-flex align-items-center flex-wrap right-controls">
          <b-form-input v-model="search" placeholder="Search companies..." class="search-input mr-1" />
          <v-select
            v-model="statusFilter"
            :clearable="false"
            :searchable="false"
            class="filter-select mr-1"
            :reduce="option => option.value"
            label="label"
            :options="statusOptions"
          />
          <v-select
            v-model="sortFilter"
            :clearable="false"
            :searchable="false"
            class="filter-select"
            :reduce="option => option.value"
            label="label"
            :options="sortOptions"
          />
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-2">
        <b-spinner label="Loading advertisers..." />
      </div>
      <b-alert v-else-if="loadError" show variant="danger" class="mb-1">
        {{ loadError }}
        <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchAdvertisers">Retry</b-button>
      </b-alert>
      <div v-else-if="!rows.length" class="text-center text-muted py-2">
        No advertisers found.
      </div>
      <b-table v-else :items="rows" :fields="fields" responsive class="mb-0 advertisers-table" tbody-tr-class="ad-row">
        <template #cell(id)="data">
          <span>{{ String(data.item.id).padStart(2, '0') }}</span>
        </template>

        <template #cell(companyName)="data">
          <div class="d-flex align-items-center">
            <b-avatar size="36" :src="data.item.logo" :text="data.item.logo ? '' : 'JK'" class="mr-1" />
            <b-link
              class="company-link"
              :to="{ name: 'advertiser-overall-details', params: { id: String(data.item.id) } }"
            >
              {{ data.item.companyName }}
            </b-link>
          </div>
        </template>

        <template #cell(followers)="data">
            <b-link
              class="followers-link"
              :to="{ name: 'advertiser-followers', params: { id: String(data.item.id) } }"
            >
            <b-avatar-group size="28">
              <b-avatar v-for="(avatar, i) in data.item.followers.slice(0, 3)" :key="i" :src="avatar" />
              <b-avatar
                v-if="data.item.extraFollowers > 0"
                variant="light-secondary"
                :text="`+${data.item.extraFollowers}`"
              />
            </b-avatar-group>
            <span class="ml-50 followers-count">{{ data.item.followersCount }}</span>
          </b-link>
        </template>

        <template #cell(status)="data">
          <span class="status-pill" :class="statusClass(data.item.accountStatus)">
            {{ data.item.accountStatus }}
          </span>
        </template>

        <template #cell(actions)="data">
          <row-actions
            show-view
            view-title="View details"
            edit-title="Edit company"
            @view="viewAdvertiser(data.item)"
            @edit="openEditCompanyModal(data.item)"
            @delete="confirmDeleteAdvertiser(data.item)"
          >
            <template #extra>
              <button
                type="button"
                class="row-action"
                title="Change password"
                @click="openPasswordModal(data.item)"
              >
                <feather-icon icon="KeyIcon" size="16" />
              </button>
              <b-dropdown
                variant="link"
                toggle-class="p-0 text-muted"
                no-caret
                right
                boundary="viewport"
              >
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" size="16" />
                </template>
                <b-dropdown-item @click="emailAdvertiser(data.item)">
                  <feather-icon icon="MailIcon" size="14" class="mr-50" />
                  Send email
                </b-dropdown-item>
                <b-dropdown-item @click="viewAdvertiserFollowers(data.item)">
                  <feather-icon icon="UsersIcon" size="14" class="mr-50" />
                  View followers
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item
                  v-if="data.item.accountStatus === 'Pending'"
                  @click="approveAdvertiser(data.item)"
                >
                  <feather-icon icon="CheckCircleIcon" size="14" class="mr-50 text-success" />
                  Approve
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="data.item.accountStatus !== 'Rejected'"
                  @click="rejectAdvertiser(data.item)"
                >
                  <feather-icon icon="XCircleIcon" size="14" class="mr-50 text-warning" />
                  Reject
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="data.item.accountStatus === 'Suspended'"
                  @click="unsuspendAdvertiser(data.item)"
                >
                  <feather-icon icon="PlayCircleIcon" size="14" class="mr-50" />
                  Unsuspend
                </b-dropdown-item>
                <b-dropdown-item
                  v-else-if="data.item.accountStatus !== 'Suspended'"
                  @click="suspendAdvertiser(data.item)"
                >
                  <feather-icon icon="PauseCircleIcon" size="14" class="mr-50" />
                  Suspend
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </row-actions>
        </template>
      </b-table>

      <AdminPaginationBar
        :page="currentPage"
        :per-page="perPageNum"
        :total="totalRows"
        @change="onPageChange"
      />
    </b-card>

    <b-modal
      v-model="showAddCompanyModal"
      centered
      hide-footer
      no-close-on-backdrop
      size="lg"
      modal-class="add-company-modal"
      @hidden="resetCompanyModal"
    >
      <div class="add-company-body">
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div>
            <h2 class="mb-25">{{ companyModalTitle }}</h2>
            <p class="text-muted mb-0">
              {{ isEditCompanyMode ? 'Update company and contact details for this advertiser account.' : 'Create a new advertiser company profile.' }}
            </p>
          </div>
          <b-badge v-if="isEditCompanyMode && editingAccountStatus" :variant="statusBadgeVariant(editingAccountStatus)">
            {{ editingAccountStatus }}
          </b-badge>
        </div>

        <div v-if="isLoadingEdit" class="text-center py-3">
          <b-spinner label="Loading advertiser..." />
        </div>

        <validation-observer v-else ref="companyFormObserver">
          <b-row>
            <b-col cols="12">
              <h5 class="section-title">Company information</h5>
            </b-col>

            <b-col cols="12">
              <mypoda-form-field
                label="Company name"
                name="Company name"
                rules="required|max:255"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.companyName"
                    :state="state"
                    placeholder="e.g. Acme Advertising Ltd."
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col cols="12">
              <h5 class="section-title">Contact information</h5>
            </b-col>

            <b-col cols="12">
              <mypoda-form-field
                label="Contact person name"
                name="Contact person name"
                rules="required|max:255"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.contactPerson"
                    :state="state"
                    placeholder="e.g. Jane Smith"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col md="6">
              <mypoda-form-field
                label="Email"
                name="Email"
                rules="required|email"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.email"
                    type="email"
                    :state="state"
                    placeholder="contact@company.com"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col md="6">
              <mypoda-form-field
                label="Contact phone"
                name="Contact phone"
                rules="required|max:30"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.contact"
                    :state="state"
                    placeholder="e.g. +1 555 123 4567"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col cols="12">
              <mypoda-form-field
                label="Address"
                name="Address"
                rules="required|max:500"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.address"
                    :state="state"
                    placeholder="Street, city, state"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col md="6">
              <mypoda-form-field
                label="Zip code"
                name="Zip code"
                rules="required|max:20"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.pinCode"
                    :state="state"
                    placeholder="e.g. 90210"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col md="6">
              <mypoda-form-field
                label="Country"
                name="Country"
                rules="required"
                required-mark
              >
                <template #default="{ state, inputId }">
                  <b-form-select
                    :id="inputId"
                    v-model="newCompany.country"
                    :options="countryOptions"
                    :state="state"
                  >
                    <template #first>
                      <b-form-select-option value="" disabled>
                        Select country
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </template>
              </mypoda-form-field>
            </b-col>

            <b-col md="6">
              <mypoda-form-field
                label="State of incorporation"
                name="State of incorporation"
                rules="max:120"
              >
                <template #default="{ state, inputId }">
                  <b-form-input
                    :id="inputId"
                    v-model="newCompany.stateOfIncorporation"
                    :state="state"
                    placeholder="e.g. Delaware"
                  />
                </template>
              </mypoda-form-field>
            </b-col>

            <template v-if="!isEditCompanyMode">
              <b-col md="6">
                <mypoda-form-field
                  label="Create password"
                  name="Password"
                  vid="password"
                  rules="required|min:8"
                  required-mark
                >
                  <template #default="{ state, inputId }">
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        :id="inputId"
                        v-model="newCompany.password"
                        :type="showCreatePassword ? 'text' : 'password'"
                        :state="state"
                        placeholder="Minimum 8 characters"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showCreatePassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showCreatePassword = !showCreatePassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                  </template>
                </mypoda-form-field>
              </b-col>

              <b-col md="6">
                <mypoda-form-field
                  label="Confirm password"
                  name="Confirm password"
                  rules="required|confirmed:password"
                  required-mark
                >
                  <template #default="{ state, inputId }">
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        :id="inputId"
                        v-model="newCompany.confirmPassword"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="state"
                        placeholder="Re-enter password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                  </template>
                </mypoda-form-field>
              </b-col>
            </template>

            <b-col cols="12" class="d-flex justify-content-end mt-1">
              <b-button variant="outline-secondary" class="mr-1" :disabled="isSavingCompany" @click="closeCompanyModal">
                Cancel
              </b-button>
              <b-button variant="primary" class="add-btn modal-action-btn" :disabled="isSavingCompany" @click="submitCompany">
                <b-spinner v-if="isSavingCompany" small class="mr-50" />
                {{ isEditCompanyMode ? 'Save changes' : 'Submit' }}
              </b-button>
            </b-col>
          </b-row>
        </validation-observer>
      </div>
    </b-modal>

    <b-modal
      v-model="showPasswordModal"
      centered
      hide-footer
      no-close-on-backdrop
      title="Change advertiser password"
      @hidden="resetPasswordModal"
    >
      <p v-if="passwordTarget" class="text-muted mb-1">
        Set a new password for <strong>{{ passwordTarget.companyName }}</strong>.
        The advertiser will need to use this password on their next login.
      </p>
      <validation-observer ref="passwordFormObserver">
        <mypoda-form-field
          label="New password"
          name="New password"
          vid="new_password"
          rules="required|min:8"
          required-mark
        >
          <template #default="{ state, inputId }">
            <b-input-group class="input-group-merge">
              <b-form-input
                :id="inputId"
                v-model="passwordForm.password"
                :type="showNewPassword ? 'text' : 'password'"
                :state="state"
                placeholder="Minimum 8 characters"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showNewPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showNewPassword = !showNewPassword"
                />
              </b-input-group-append>
            </b-input-group>
          </template>
        </mypoda-form-field>

        <mypoda-form-field
          label="Confirm new password"
          name="Confirm new password"
          rules="required|confirmed:new_password"
          required-mark
        >
          <template #default="{ state, inputId }">
            <b-form-input
              :id="inputId"
              v-model="passwordForm.passwordConfirmation"
              :type="showNewPassword ? 'text' : 'password'"
              :state="state"
              placeholder="Re-enter new password"
            />
          </template>
        </mypoda-form-field>

        <div class="d-flex justify-content-end mt-1">
          <b-button variant="outline-secondary" class="mr-1" :disabled="isSavingPassword" @click="showPasswordModal = false">
            Cancel
          </b-button>
          <b-button variant="primary" :disabled="isSavingPassword" @click="submitPasswordChange">
            <b-spinner v-if="isSavingPassword" small class="mr-50" />
            Update password
          </b-button>
        </div>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BAvatarGroup,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import RowActions from '@/components/admin/RowActions.vue'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  applyListPagination,
  debounce,
  formatAdminDate,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'
import {
  confirmAction,
  notifyError,
  notifyInfo,
  notifySuccess,
  promptRejectReason,
} from '@/utils/uiActions'
import { ADMIN_COUNTRY_OPTIONS } from '@/constants/countries'

export default {
  components: {
    BAlert,
    BAvatar,
    BAvatarGroup,
    BBadge,
    BButton,
    BCard,
    BCol,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BModal,
    BRow,
    BSpinner,
    BTable,
    ValidationObserver,
    MypodaFormField,
    vSelect,
    AdminPaginationBar,
    RowActions,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      currentPage: 1,
      totalRows: 0,
      showAddCompanyModal: false,
      isEditCompanyMode: false,
      editingCompanyId: null,
      editingAccountStatus: '',
      isLoadingEdit: false,
      isSavingCompany: false,
      showPasswordModal: false,
      passwordTarget: null,
      passwordForm: {
        password: '',
        passwordConfirmation: '',
      },
      isSavingPassword: false,
      showNewPassword: false,
      showCreatePassword: false,
      showConfirmPassword: false,
      perPage: '08',
      search: '',
      statusFilter: '',
      sortFilter: '-joined_date',
      statusOptions: [
        { label: 'All statuses', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Blocked', value: 'blocked' },
      ],
      sortOptions: [
        { label: 'Newest first', value: '-joined_date' },
        { label: 'Oldest joined', value: 'joined_date' },
        { label: 'Company A–Z', value: 'company_name' },
        { label: 'Company Z–A', value: '-company_name' },
      ],
      newCompany: {
        companyName: '',
        contactPerson: '',
        address: '',
        pinCode: '',
        country: '',
        stateOfIncorporation: '',
        email: '',
        contact: '',
        password: '',
        confirmPassword: '',
      },
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'companyName', label: 'COMPANY NAME' },
        { key: 'followers', label: 'FOLLOWERS' },
        { key: 'joined', label: 'JOINED' },
        { key: 'adsPosted', label: 'ADS POSTED' },
        { key: 'activeAds', label: 'ACTIVE ADS' },
        { key: 'status', label: 'STATUS' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      rows: [],
    }
  },
  computed: {
    companyModalTitle() {
      return this.isEditCompanyMode ? 'Edit Advertiser' : 'Add New Company'
    },
    countryOptions() {
      return ADMIN_COUNTRY_OPTIONS
    },
    perPageNum() {
      return parseInt(this.perPage, 10) || 8
    },
  },
  watch: {
    perPage() {
      this.reloadPageOne()
    },
    search() {
      this.debouncedSearch()
    },
    statusFilter() {
      this.reloadPageOne()
    },
    sortFilter() {
      this.reloadPageOne()
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.currentPage = 1
      this.fetchAdvertisers()
    }, 400)
    this.fetchAdvertisers()
  },
  methods: {
    reloadPageOne() {
      this.currentPage = 1
      this.fetchAdvertisers()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchAdvertisers()
    },
    formatCount(n) {
      const num = Number(n) || 0
      if (num >= 1000) return `${Math.round(num / 100) / 10}K`
      return String(num)
    },
    mapAdvertiserRow(item) {
      const preview = (item.followers?.preview || []).map(p => resolveMediaUrl(p.profile_image))
      const count = Number(item.followers?.count || 0)
      return {
        id: item.id,
        ownerUserId: item.owner_user_id || item.ownerUserId || item.id,
        companyName: item.company_name,
        logo: resolveMediaUrl(item.logo),
        followers: preview,
        extraFollowers: Math.max(0, count - 3),
        followersCount: this.formatCount(count),
        joined: formatAdminDate(item.joined_date),
        adsPosted: item.ads_posted ?? 0,
        activeAds: item.active_ads ?? 0,
        accountStatus: this.resolveAdvertiserAccountStatus(item),
        contactPerson: item.contact_person || item.contact_name || item.owner_name || '',
        address: item.address || '',
        pinCode: item.pin_code || item.pincode || '',
        country: item.country?.name || item.country_name || item.country || '',
        email: item.email || item.company_email || item.contact_email || '',
        contact: item.phone || item.contact_phone || item.mobile || '',
      }
    },
    statusClass(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'approved' || normalized === 'active') return 'status-approved'
      if (normalized === 'pending') return 'status-pending'
      if (normalized === 'rejected' || normalized === 'blocked') return 'status-rejected'
      if (normalized === 'suspended') return 'status-suspended'
      return 'status-pending'
    },
    statusBadgeVariant(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'approved' || normalized === 'active') return 'success'
      if (normalized === 'pending') return 'warning'
      if (normalized === 'rejected' || normalized === 'blocked') return 'danger'
      if (normalized === 'suspended') return 'secondary'
      return 'light'
    },
    formatAccountStatus(status) {
      const normalized = String(status || 'pending').toLowerCase()
      if (normalized === 'approved') return 'Approved'
      if (normalized === 'active') return 'Active'
      if (normalized === 'rejected') return 'Rejected'
      if (normalized === 'suspended') return 'Suspended'
      if (normalized === 'blocked') return 'Blocked'
      return 'Pending'
    },
    resolveAdvertiserAccountStatus(data, listStatus = '') {
      if (!data) {
        return this.formatAccountStatus(listStatus)
      }
      const accountStatusText = String(
        data.account_status || data.accountStatus || listStatus || '',
      ).toLowerCase()
      const isSuspended = Boolean(data.is_suspended)
        || accountStatusText === 'suspended'
        || String(data.status || '').toLowerCase() === 'suspended'
      if (isSuspended) {
        return this.formatAccountStatus('suspended')
      }
      const raw = data.account_status
        || data.accountStatus
        || listStatus
        || data.status
        || 'pending'
      return this.formatAccountStatus(raw)
    },
    emptyCompanyForm() {
      return {
        companyName: '',
        contactPerson: '',
        address: '',
        pinCode: '',
        country: '',
        stateOfIncorporation: '',
        email: '',
        contact: '',
        password: '',
        confirmPassword: '',
      }
    },
    buildCompanyPayload() {
      return {
        company_name: this.newCompany.companyName.trim(),
        contact_person_name: this.newCompany.contactPerson.trim(),
        address: this.newCompany.address.trim(),
        pin_code: this.newCompany.pinCode.trim(),
        country: this.newCompany.country,
        state_of_incorporation: String(this.newCompany.stateOfIncorporation || '').trim(),
        email: this.newCompany.email.trim(),
        contact: this.newCompany.contact.trim(),
      }
    },
    async fetchAdvertisers() {
      this.isLoading = true
      this.loadError = ''
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPageNum,
          sort: this.sortFilter,
        }
        const term = this.search.trim()
        if (term) params.search = term
        if (this.statusFilter) params.status = this.statusFilter

        const res = await adminPanelApi.advertisers(params)
        const payload = unwrapAdminResponse(res)
        applyListPagination(this, res, payload)
        this.rows = (payload.advertisers || []).map(a => this.mapAdvertiserRow(a))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load advertisers.'
        this.rows = []
        this.totalRows = 0
      } finally {
        this.isLoading = false
      }
    },
    openAddCompanyModal() {
      this.isEditCompanyMode = false
      this.editingCompanyId = null
      this.editingAccountStatus = ''
      this.newCompany = this.emptyCompanyForm()
      this.showAddCompanyModal = true
    },
    async openEditCompanyModal(item) {
      this.isEditCompanyMode = true
      this.editingCompanyId = item.id
      this.editingAccountStatus = this.resolveAdvertiserAccountStatus(item, item.accountStatus)
      this.isLoadingEdit = true
      this.showAddCompanyModal = true
      try {
        const res = await adminPanelApi.advertiserDetails(item.id)
        const detail = unwrapAdminResponse(res)
        this.newCompany = {
          companyName: detail.company_name || item.companyName || '',
          contactPerson: detail.contact_person_name || item.contactPerson || '',
          address: detail.address || item.address || '',
          pinCode: detail.pincode || detail.pin_code || item.pinCode || '',
          country: detail.city || detail.country || item.country || '',
          stateOfIncorporation: detail.state_of_incorporation || '',
          email: detail.email || item.email || '',
          contact: detail.contact || item.contact || '',
          password: '',
          confirmPassword: '',
        }
        this.editingAccountStatus = this.resolveAdvertiserAccountStatus(detail, item.accountStatus)
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to load advertiser details.')
        this.newCompany = {
          companyName: item.companyName || '',
          contactPerson: item.contactPerson || '',
          address: item.address || '',
          pinCode: item.pinCode || '',
          country: item.country || '',
          stateOfIncorporation: '',
          email: item.email || '',
          contact: item.contact || '',
          password: '',
          confirmPassword: '',
        }
        this.editingAccountStatus = this.resolveAdvertiserAccountStatus(item, item.accountStatus)
      } finally {
        this.isLoadingEdit = false
      }
    },
    closeCompanyModal() {
      this.showAddCompanyModal = false
    },
    resetCompanyModal() {
      this.isEditCompanyMode = false
      this.editingCompanyId = null
      this.editingAccountStatus = ''
      this.isLoadingEdit = false
      this.isSavingCompany = false
      this.newCompany = this.emptyCompanyForm()
      this.showCreatePassword = false
      this.showConfirmPassword = false
      if (this.$refs.companyFormObserver) {
        this.$refs.companyFormObserver.reset()
      }
    },
    async submitCompany() {
      const valid = await this.$refs.companyFormObserver.validate()
      if (!valid) return
      if (this.isEditCompanyMode) {
        this.isSavingCompany = true
        try {
          await adminPanelApi.updateAdvertiser(this.editingCompanyId, this.buildCompanyPayload())
          notifySuccess(this, 'Advertiser updated successfully.')
          this.closeCompanyModal()
          await this.fetchAdvertisers()
        } catch (e) {
          notifyError(this, e.response?.data?.message || 'Unable to update advertiser.')
        } finally {
          this.isSavingCompany = false
        }
        return
      }
      this.isSavingCompany = true
      try {
        await adminPanelApi.createAdvertiser({
          ...this.buildCompanyPayload(),
          password: this.newCompany.password,
          password_confirmation: this.newCompany.confirmPassword,
        })
        notifySuccess(this, 'Advertiser created successfully.')
        this.closeCompanyModal()
        await this.fetchAdvertisers()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to create advertiser.')
      } finally {
        this.isSavingCompany = false
      }
    },
    openPasswordModal(item) {
      this.passwordTarget = item
      this.passwordForm = { password: '', passwordConfirmation: '' }
      this.showNewPassword = false
      this.showPasswordModal = true
    },
    resetPasswordModal() {
      this.passwordTarget = null
      this.passwordForm = { password: '', passwordConfirmation: '' }
      this.isSavingPassword = false
      this.showNewPassword = false
      if (this.$refs.passwordFormObserver) {
        this.$refs.passwordFormObserver.reset()
      }
    },
    async submitPasswordChange() {
      const valid = await this.$refs.passwordFormObserver.validate()
      if (!valid || !this.passwordTarget?.id) return
      this.isSavingPassword = true
      try {
        await adminPanelApi.changeAdvertiserPassword(this.passwordTarget.id, {
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.passwordConfirmation,
        })
        notifySuccess(this, 'Password updated successfully.')
        this.showPasswordModal = false
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to update password.')
      } finally {
        this.isSavingPassword = false
      }
    },
    viewAdvertiser(item) {
      this.$router.push({ name: 'advertiser-overall-details', params: { id: String(item.id) } })
    },
    viewAdvertiserFollowers(item) {
      this.$router.push({ name: 'advertiser-followers', params: { id: String(item.id) } })
    },
    approveAdvertiser(item) {
      confirmAction(
        this,
        {
          title: 'Approve advertiser?',
          text: `Approve "${item.companyName}"? They will be able to access their account.`,
          confirmButtonText: 'Approve',
        },
        async () => {
          try {
            await adminPanelApi.approveAdvertiser(item.id)
            notifySuccess(this, 'Advertiser approved successfully.')
            await this.fetchAdvertisers()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to approve advertiser.')
          }
        },
      )
    },
    async rejectAdvertiser(item) {
      const reason = await promptRejectReason(this, {
        title: 'Reject advertiser',
        text: `Provide a reason for rejecting "${item.companyName}".`,
      })
      if (!reason) return
      try {
        await adminPanelApi.rejectAdvertiser({ id: item.id, reason })
        notifySuccess(this, 'Advertiser rejected successfully.')
        await this.fetchAdvertisers()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to reject advertiser.')
      }
    },
    async suspendAdvertiser(item) {
      const reason = await promptRejectReason(this, {
        title: 'Suspend advertiser',
        text: `Provide a reason for suspending "${item.companyName}".`,
      })
      if (!reason) return
      try {
        await adminPanelApi.suspendAdvertiser({ id: item.id, reason })
        notifySuccess(this, 'Advertiser suspended successfully.')
        await this.fetchAdvertisers()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to suspend advertiser.')
      }
    },
    unsuspendAdvertiser(item) {
      confirmAction(
        this,
        {
          title: 'Unsuspend advertiser?',
          text: `Restore access for "${item.companyName}"?`,
          confirmButtonText: 'Unsuspend',
        },
        async () => {
          try {
            await adminPanelApi.unsuspendAdvertiser(item.id)
            notifySuccess(this, 'Advertiser unsuspended successfully.')
            await this.fetchAdvertisers()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to unsuspend advertiser.')
          }
        },
      )
    },
    emailAdvertiser(item) {
      const email = item.email || ''
      if (email && email.includes('@')) {
        window.open(`mailto:${email}`, '_blank', 'noopener')
        return
      }
      notifyInfo(this, `No email on file for ${item.companyName}. Open advertiser details to view contact info.`, 'Email')
    },
    confirmDeleteAdvertiser(item) {
      confirmAction(
        this,
        {
          title: 'Delete advertiser?',
          text: `Remove ${item.companyName}? This cannot be undone.`,
          confirmButtonText: 'Delete',
        },
        async () => {
          try {
            await adminPanelApi.deleteAdvertiser(item.id)
            notifySuccess(this, 'Advertiser removed successfully.')
            await this.fetchAdvertisers()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete advertiser.')
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.advertisers-page {
  padding-top: 0.35rem;
}

.entries-select {
  width: 82px;
  min-width: 82px;
}

.filter-select {
  width: 160px;
  min-width: 150px;
}

::v-deep .filter-select .vs__selected-options {
  flex-wrap: nowrap;
}

::v-deep .filter-select .vs__selected {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #4b465c;
  font-weight: 500;
}

::v-deep .entries-select .vs__selected {
  color: #4b465c;
  font-weight: 600;
}

.search-input {
  width: 180px;
}

.add-btn {
  background: #0b1f39 !important;
  border-color: #0b1f39 !important;
  min-height: 38px;
  padding-left: 0.95rem;
  padding-right: 0.95rem;
}

.company-link {
  color: #4b465c;
  font-weight: 500;
}

.followers-link {
  display: inline-flex;
  align-items: center;
  color: #6e6b7b;
}

.followers-count {
  font-size: 0.85rem;
  font-weight: 500;
}

.add-company-body {
  padding: 0.5rem 1rem 0.75rem;
}

.add-company-body h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #4b465c;
}

.membership-title {
  color: #4b465c;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0.3rem 0 0.55rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #4b465c;
  margin: 0.75rem 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.action-icons {
  gap: 0.15rem;
}

.modal-label {
  font-size: 0.82rem;
  color: #6e6b7b;
  margin-bottom: 0.65rem;
}

.modal-action-btn {
  min-width: 150px;
}

.top-toolbar {
  row-gap: 0.75rem;
}

.left-controls {
  column-gap: 0.65rem;
}

.right-controls {
  column-gap: 0.65rem;
}

.status-pill {
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-active {
  background: #e8f4da;
  color: #5f8b2f;
}

.status-approved {
  background: #e8f4da;
  color: #5f8b2f;
}

.status-pending {
  background: #fff4e0;
  color: #b78103;
}

.status-rejected {
  background: #fde7e7;
  color: #e54a4a;
}

.status-suspended {
  background: #ececee;
  color: #6e6b7b;
}

.status-inactive {
  background: #fde7e7;
  color: #e54a4a;
}

::v-deep .advertisers-table th {
  font-size: 0.76rem;
  letter-spacing: 0.04rem;
  color: #6e6b7b;
}

::v-deep .advertisers-table td,
::v-deep .advertisers-table th {
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  vertical-align: middle;
}

::v-deep .page-item.active .page-link {
  background-color: #0b1f39 !important;
  border-color: #0b1f39 !important;
}

@media (max-width: 767px) {
  .left-controls {
    flex-wrap: nowrap;
    width: 100%;
  }

  .add-btn {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.84rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .right-controls {
    width: 100%;
  }

  .search-input,
  .filter-select {
    width: 100%;
  }
}

</style>
